import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import TitleBlock from "@/components/TitleBlock";
import Callout from "@/components/Callout";
import {
  RefreshCw, Search, ExternalLink, TrendingUp, MousePointerClick, Eye, Plug,
} from "lucide-react";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const WINDOWS = [
  { value: 7,  label: "7d"  },
  { value: 28, label: "28d" },
  { value: 90, label: "90d" },
];

function fmtPct(v) { return `${(v * 100).toFixed(1)}%`; }
function fmtPos(v) { return v > 0 ? v.toFixed(1) : "—"; }
function fmtNum(v) { return (v || 0).toLocaleString(); }

function KPI({ label, value, sub, testid }) {
  return (
    <div className="bg-warm-tint border-t-4 border-cobalt p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] opacity-60">{label}</div>
      <div className="mt-1 text-3xl font-bold text-navy">{value}</div>
      {sub && <div className="text-xs text-midnight opacity-70 mt-1">{sub}</div>}
    </div>
  );
}

export default function SeoKeywordsPage() {
  const [days, setDays] = useState(28);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/seo/keywords?days=${d}&top=50`);
      setData(data);
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Failed to load keywords." });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onWindow = (d) => { setDays(d); load(d); };

  const sync = async () => {
    setSyncing(true); setMsg(null);
    try {
      const { data } = await api.post("/integrations/google-seo/sync/run-now");
      if (data?.skipped) {
        setMsg({ kind: "err", text: `Skipped: ${data.reason}. Connect Google in Integrations first.` });
      } else if (data?.ok) {
        setMsg({ kind: "ok", text: `Synced ${data.gsc_synced} GSC rows + ${data.ga4_synced} GA4 days.` });
        await load(days);
      } else {
        setMsg({ kind: "err", text: (data?.errors || []).join("; ") || "Sync failed." });
      }
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Sync failed." });
    } finally { setSyncing(false); }
  };

  const connected = data?.connected;
  const totals = data?.totals || {};
  const top = data?.top_queries || [];
  const trend = data?.trend || [];

  return (
    <div data-testid="seo-keywords-page">
      <TitleBlock
        eyebrow="SEO · Phase 2B"
        title="Keywords & Rankings"
        subtitle="Live Search Console data — what queries you rank for, how often you show up, and where you sit on page 1."
        right={
          <button onClick={sync} disabled={syncing || !connected}
                  className="btn-yellow inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="keywords-sync-btn">
            <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync now"}
          </button>
        }
      />

      <div className="p-6 md:p-8 space-y-6">
        {msg && (
          <div className="callout text-sm" style={msg.kind === "err" ? { borderTopColor: "#b91c1c" } : {}} data-testid="keywords-msg">
            {msg.text}
          </div>
        )}

        {!connected && !loading && (
          <Callout title="Google Search Console not connected" testid="keywords-empty">
            Hop over to <Link to="/integrations" className="underline text-cobalt font-bold">Integrations</Link> and click <strong>Connect Google</strong> on the &ldquo;SEO · search &amp; organic traffic&rdquo; card. One sign-in unlocks both this page and Organic Traffic.
            <div className="mt-3">
              <Link to="/integrations" className="btn-yellow inline-flex items-center gap-2" data-testid="keywords-go-integrations">
                <Plug className="w-4 h-4" /> Go to Integrations
              </Link>
            </div>
          </Callout>
        )}

        {/* Window toggle */}
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.22em] opacity-60">Window</span>
          {WINDOWS.map((w) => (
            <button key={w.value} onClick={() => onWindow(w.value)}
                    data-testid={`keywords-window-${w.value}`}
                    className={`text-xs px-3 py-1 border ${days === w.value ? "bg-navy text-warm border-navy" : "bg-warm-tint text-navy border-navy/30 hover:bg-yellow/20"}`}>
              {w.label}
            </button>
          ))}
          {data?.window && (
            <span className="ml-auto text-[11px] opacity-60 italic" data-testid="keywords-window-label">
              {data.window.start} → {data.window.end} · {data.window.note}
            </span>
          )}
        </div>

        {/* KPI tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI label="Total Clicks"        value={fmtNum(totals.clicks)}
               sub={`from organic search`}        testid="kpi-clicks" />
          <KPI label="Total Impressions"   value={fmtNum(totals.impressions)}
               sub="times you showed up on Google" testid="kpi-impressions" />
          <KPI label="Avg CTR"             value={totals.impressions ? fmtPct(totals.ctr || 0) : "—"}
               sub="clicks ÷ impressions"          testid="kpi-ctr" />
          <KPI label="Unique Queries"      value={fmtNum(totals.unique_queries)}
               sub="distinct search terms"         testid="kpi-queries" />
        </div>

        {/* Clicks/impressions trend */}
        <div className="bg-warm-tint border-t-4 border-cobalt p-6" data-testid="keywords-trend-chart">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="text-xl font-bold text-navy">Daily Clicks &amp; Impressions</h2>
            <span className="text-xs uppercase tracking-widest text-cobalt">From Search Console</span>
          </div>
          {trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trend}>
                <CartesianGrid stroke="#141B4D" strokeOpacity={0.1} vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#101820", fontSize: 11 }}
                       tickFormatter={(d) => d.slice(5)} />
                <YAxis yAxisId="left" tick={{ fill: "#101820", fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right"
                       tick={{ fill: "#101820", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#141B4D", border: "none", color: "#F2E9DB", padding: "8px 12px" }}
                         itemStyle={{ color: "#F2E9DB" }}
                         labelStyle={{ color: "#FFD100", fontWeight: 700, marginBottom: 4 }} />
                <Legend wrapperStyle={{ fontSize: 12, color: "#101820" }} />
                <Line yAxisId="left"  type="monotone" dataKey="clicks"      name="Clicks"
                      stroke="#1D57A5" strokeWidth={2} dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="impressions" name="Impressions"
                      stroke="#FFD100" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-sm opacity-60 italic">No data yet — click <strong>Sync now</strong> above.</div>
          )}
        </div>

        {/* Top queries table */}
        <div className="bg-warm-tint border-t-4 border-yellow p-6" data-testid="keywords-table">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-xl font-bold text-navy flex items-center gap-2">
              <Search className="w-5 h-5" /> Top {top.length} Queries (last {days} days)
            </h2>
            <span className="text-xs uppercase tracking-widest text-cobalt">Sorted by impressions</span>
          </div>
          {top.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-[0.22em] text-cobalt border-b border-navy/20">
                  <tr>
                    <th className="text-left py-2">Query</th>
                    <th className="text-right py-2"><MousePointerClick className="w-3 h-3 inline" /> Clicks</th>
                    <th className="text-right py-2"><Eye className="w-3 h-3 inline" /> Impressions</th>
                    <th className="text-right py-2">CTR</th>
                    <th className="text-right py-2"><TrendingUp className="w-3 h-3 inline" /> Avg Pos.</th>
                  </tr>
                </thead>
                <tbody>
                  {top.map((q) => (
                    <tr key={q.query} className="border-b border-warm last:border-0" data-testid={`keyword-row`}>
                      <td className="py-2 pr-3 text-navy font-medium">
                        <a href={`https://www.google.com/search?q=${encodeURIComponent(q.query)}`}
                           target="_blank" rel="noopener noreferrer"
                           className="hover:underline inline-flex items-center gap-1">
                          {q.query}
                          <ExternalLink className="w-3 h-3 opacity-50" />
                        </a>
                      </td>
                      <td className="py-2 text-right tabular-nums">{fmtNum(q.clicks)}</td>
                      <td className="py-2 text-right tabular-nums">{fmtNum(q.impressions)}</td>
                      <td className="py-2 text-right tabular-nums">{fmtPct(q.ctr)}</td>
                      <td className={`py-2 text-right tabular-nums font-bold ${q.position > 0 && q.position <= 10 ? "text-evergreen" : q.position > 10 && q.position <= 20 ? "text-orange-700" : "text-midnight"}`}>
                        {fmtPos(q.position)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-sm opacity-60 italic" data-testid="keywords-empty-table">No keyword data yet.</div>
          )}
          {data?.last_synced && (
            <div className="mt-3 text-xs opacity-60">Last synced: {new Date(data.last_synced).toLocaleString()}</div>
          )}
        </div>

        <Callout title="How to read this">
          <strong>Position 1-10</strong> means you&rsquo;re on page 1 — green. <strong>11-20</strong> is page 2 — yellow, ripe to push. <strong>20+</strong> is buried. <em>Avg Pos.</em> is impression-weighted, so high-impression queries dominate. Click any query to see what Google shows for it today.
        </Callout>
      </div>
    </div>
  );
}
